import { baseApi } from '@/store/baseApi';
import type { IGetNotificationsResponse, IListNotificationsParams } from './interfaces';

const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<IGetNotificationsResponse, IListNotificationsParams | void>({
      query: (params) => ({ url: '/notifications', params: params ?? undefined }),
      transformResponse: (response: { data: IGetNotificationsResponse }) => response.data,
      // Collapse every page into one cache entry keyed only by endpoint, so pages accumulate
      // instead of overwriting each other.
      serializeQueryArgs: ({ endpointName }) => endpointName,
      // Idempotent: a retry of an already-merged cursor must not append the page twice.
      // No cursor means a first-page load or a forced re-sync, so replace rather than append.
      merge: (currentCache, newResponse, { arg }) => {
        if (!arg?.cursor) return newResponse;
        const seen = new Set(currentCache.items.map((item) => item.id));
        currentCache.items.push(...newResponse.items.filter((item) => !seen.has(item.id)));
        currentCache.nextCursor = newResponse.nextCursor;
        currentCache.hasMore = newResponse.hasMore;
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      // Deliberately not invalidated by the mutations below — that would refetch with the
      // current cursor and drop every loaded page. The optimistic updates keep this entry in
      // sync instead, and the hook re-syncs it on open via `refetchOnMountOrArgChange`.
      providesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),
    getUnreadNotificationsCount: builder.query<number, void>({
      query: () => ({ url: '/notifications/unread-count' }),
      transformResponse: (response: { data: { count: number } }) => response.data.count,
      providesTags: [{ type: 'Notifications', id: 'UNREAD_COUNT' }],
    }),
    markNotificationAsRead: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/notifications/${id}/read`, method: 'PATCH' }),
      invalidatesTags: [{ type: 'Notifications', id: 'UNREAD_COUNT' }],
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getNotifications', undefined, (draft) => {
            const notification = draft.items.find((item) => item.id === id);
            if (notification) notification.isRead = true;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    markAllNotificationsAsRead: builder.mutation<void, void>({
      query: () => ({ url: '/notifications/read-all', method: 'PATCH' }),
      invalidatesTags: [{ type: 'Notifications', id: 'UNREAD_COUNT' }],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          notificationsApi.util.updateQueryData('getNotifications', undefined, (draft) => {
            draft.items.forEach((item) => {
              item.isRead = true;
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadNotificationsCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
} = notificationsApi;
